import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { ScrollReveal, Overline } from "./Reveal";
import { getPromo, setPromo, clearPromo, subscribePromo } from "../promo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    name: "Your First Pack, For Less.",
    price: "$14.95",
    strike: "$27.95",
    badge: "Introductory price",
    per: "one-time — 30 strips",
    desc: "30 premium nasal strips. One simple habit.",
    bullets: ["One-time purchase", "No subscription required", "30 strips included"],
    lookup: "n001_trial",
    testid: "pricing-first-pack-card",
    button: "pricing-first-pack-button",
    emailTestid: "pricing-first-pack-email-input",
    featured: true,
    ribbon: "Recommended offer",
  },
  {
    name: "The Subscription",
    price: "$23.95",
    strike: "$27.95",
    badge: null,
    per: "per pack — monthly, 30 strips",
    desc: "Save 14% every month. Never run dry. Cancel anytime.",
    bullets: ["Free postage every month", "Founding price locked for life", "Pause or cancel anytime"],
    lookup: "n001_monthly",
    testid: "pricing-subscription-card",
    button: "pricing-subscription-button",
    emailTestid: null,
    featured: false,
    ribbon: null,
  },
];

const Pricing = () => {
  const [loading, setLoading] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromoState] = useState(getPromo());
  const [applying, setApplying] = useState(false);
  const [firstPackEmail, setFirstPackEmail] = useState("");
  const [packQty, setPackQty] = useState(1);
  const [shipCfg, setShipCfg] = useState(null);

  // First pack $14.95 intro, additional packs at the normal $27.95
  // with multi-pack tiers applied to the extras (3+ packs −10%, 5+ −15%)
  const tierPct = packQty >= 5 ? 15 : packQty >= 3 ? 10 : 0;
  const extraUnit = Math.round(2795 * (100 - tierPct) / 100) / 100;
  const multiTotal = 14.95 + extraUnit * (packQty - 1);

  // Active promo: percent codes reprice on the page, fixed codes note the saving
  const pct = promo?.percent_off || 0;
  const money = (v) => v.toFixed(2);
  const firstNow = 14.95 * (1 - pct / 100);
  const subNow = 23.95 * (1 - pct / 100);
  const multiNow = multiTotal * (1 - pct / 100);

  const applyCode = async (raw) => {
    const c = (raw ?? promoCode).trim().toUpperCase();
    if (!c || applying) return;
    setApplying(true);
    try {
      const { data } = await axios.get(`${API}/promo/validate`, { params: { code: c } });
      if (data.valid) {
        setPromo({ code: c, percent_off: data.percent_off, amount_off: data.amount_off, kind: data.kind });
        toast.success(
          `Code ${c} active — ${data.percent_off ? `${data.percent_off}% off` : `$${data.amount_off} off`} your order.`
        );
      } else {
        clearPromo();
        toast.error(data.reason || "That code didn't work.");
      }
    } catch {
      toast.error("Couldn't check that code — try again.");
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => subscribePromo(setPromoState), []);

  useEffect(() => {
    axios.get(`${API}/shipping/settings`)
      .then(({ data }) => setShipCfg(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setPromoCode(ref.toUpperCase());
      applyCode(ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (lookup, qty = 1) => {
    setLoading(lookup);
    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      inIframe = true;
    }
    const tab = inIframe ? window.open("", "_blank") : null;
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookup,
        quantity: qty,
        origin_url: window.location.origin,
        discount_code: promoCode.trim(),
        buyer_email: (lookup === "n001_trial" ? firstPackEmail : "").trim(),
      });
      if (tab) {
        tab.location.href = data.checkout_url;
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      if (tab) tab.close();
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Checkout failed — try again.");
      setLoading(null);
    }
  };

  return (
  <section
    id="pricing"
    data-testid="pricing-section"
    className="relative py-28 md:py-40 px-6 md:px-10 border-t border-white/10"
  >
    <div className="max-w-[1600px] mx-auto">
      <ScrollReveal>
        <Overline className="mb-4">Pricing — Pick Your Poison</Overline>
        <h2 className="font-anton uppercase text-4xl md:text-6xl leading-[0.9]">
          Two ways in<span className="text-volt">.</span>
        </h2>
      </ScrollReveal>

      <ScrollReveal delay={0.1}>
        <div
          data-testid="pricing-founders-banner"
          className="mt-12 border border-volt bg-volt/5 px-6 md:px-8 py-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-8"
        >
          <span className="font-anton uppercase text-xl md:text-2xl text-volt">
            Introductory price
          </span>
          <span className="text-white/70 font-light text-base md:text-lg">
            Your first pack for <span className="text-white">$14.95</span> (usually{" "}
            <span className="text-white">$27.95</span>) — one-time, no subscription.
          </span>
        </div>
      </ScrollReveal>

      <ScrollReveal delay={0.12}>
        <div className="mt-6 max-w-xl">
          <div className="flex">
            <input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && applyCode()}
              placeholder="GOT A CODE? ENTER IT HERE"
              data-testid="pricing-promo-input"
              className="flex-1 min-w-0 h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-xs uppercase tracking-[0.2em] text-white placeholder:text-white/30 outline-none transition-colors duration-300"
            />
            <button
              onClick={() => applyCode()}
              disabled={applying || !promoCode.trim()}
              data-testid="pricing-promo-apply"
              className="h-12 px-6 bg-volt text-black font-anton uppercase tracking-wider text-sm shrink-0 transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-50"
            >
              {applying ? "…" : "Apply"}
            </button>
          </div>
          {promo && (
            <p data-testid="pricing-promo-active" className="mt-2 font-plex text-[10px] uppercase tracking-[0.2em] text-volt">
              Code {promo.code} active —{" "}
              {promo.percent_off ? `${promo.percent_off}% off — prices updated below` : `$${promo.amount_off} off your order`}
            </p>
          )}
          <p className="mt-2 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
            No email needed — we grab it at checkout
          </p>
          <p data-testid="pricing-shipping-note" className="mt-2 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
            {shipCfg?.free_all ? (
              <>Postage: <span className="text-volt">free on every order</span></>
            ) : (
              <>
                Postage:{" "}
                <span className="text-white/70">${((shipCfg?.fee_cents ?? 799) / 100).toFixed(2)} flat</span> —{" "}
                <span className="text-volt">free over ${(((shipCfg?.free_threshold_cents ?? 5000) / 100)).toFixed(2).replace(/\.00$/, "")}</span>{" "}
                after codes
              </>
            )}
          </p>
        </div>
      </ScrollReveal>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {PLANS.map((plan, i) => (
          <ScrollReveal key={plan.name} delay={0.15 + i * 0.1}>
            <div
              data-testid={plan.testid}
              className={`relative border p-8 md:p-12 h-full flex flex-col transition-colors duration-300 ${
                plan.featured
                  ? "border-volt bg-[#121212]"
                  : "border-white/10 bg-[#121212] hover:border-white/30"
              }`}
            >
              {plan.ribbon && (
                <span className="absolute -top-px right-0 bg-volt text-black font-plex text-[10px] uppercase tracking-[0.25em] px-4 py-2">
                  {plan.ribbon}
                </span>
              )}
              <p className="font-plex text-xs uppercase tracking-[0.25em] text-white/50">
                Breethe · 30 nasal strips
              </p>
              <p className="mt-3 font-anton uppercase text-2xl md:text-4xl leading-none">
                {plan.name}
              </p>
              <div className="mt-6 flex items-baseline gap-3 flex-wrap">
                <span className="font-anton text-6xl md:text-8xl leading-none">{plan.price}</span>
                <span className="font-anton text-2xl md:text-3xl text-white/30 line-through">
                  {plan.strike}
                </span>
                {plan.badge && (
                  <span className="font-plex text-[10px] uppercase tracking-[0.2em] text-volt border border-volt/60 px-3 py-1.5">
                    {plan.badge}
                  </span>
                )}
              </div>
              <p className="mt-2 font-plex text-xs uppercase tracking-[0.25em] text-white/40">
                {plan.per}
              </p>
              {promo && (
                <p data-testid={`${plan.testid}-promo-price`} className="mt-2 font-plex text-xs uppercase tracking-[0.2em] text-volt">
                  With code {promo.code}:{" "}
                  {promo.percent_off
                    ? plan.lookup === "n001_monthly"
                      ? `$${money(subNow)}/mo`
                      : `$${money(firstNow)}`
                    : `$${promo.amount_off} off at checkout`}
                </p>
              )}
              <p className="mt-6 text-white/60 font-light text-base md:text-lg">{plan.desc}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {plan.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-white/70 font-light text-base">
                    <Check size={16} className="text-volt shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              {plan.lookup === "n001_trial" && (
                <div data-testid="first-pack-qty-box" className="mt-6 border border-white/10 p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/50">
                      Quantity
                    </span>
                    <div className="flex items-center border border-white/20">
                      <button
                        onClick={() => setPackQty((q) => Math.max(1, q - 1))}
                        disabled={packQty <= 1}
                        data-testid="first-pack-qty-minus"
                        aria-label="Decrease quantity"
                        className="h-9 w-9 font-anton text-lg text-white/70 transition-colors duration-200 hover:bg-white hover:text-black disabled:opacity-30"
                      >
                        −
                      </button>
                      <span data-testid="first-pack-qty-value" className="w-10 text-center font-anton text-lg leading-none">
                        {packQty}
                      </span>
                      <button
                        onClick={() => setPackQty((q) => Math.min(20, q + 1))}
                        disabled={packQty >= 20}
                        data-testid="first-pack-qty-plus"
                        aria-label="Increase quantity"
                        className="h-9 w-9 font-anton text-lg text-white/70 transition-colors duration-200 hover:bg-white hover:text-black disabled:opacity-30"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 font-plex text-[10px] uppercase tracking-[0.2em] text-volt/80">
                    {packQty === 1
                      ? "First pack at intro price — one per customer"
                      : `First pack $14.95 · extras $${extraUnit.toFixed(2)} each${tierPct > 0 ? ` (${tierPct}% off)` : ""} · 3+ −10% · 5+ −15%`}
                  </p>
                  {packQty > 1 && (
                    <p data-testid="first-pack-qty-total" className="mt-2 font-plex text-xs text-white/70">
                      {packQty} packs = ${multiTotal.toFixed(2)}
                      {" — "}save{" "}
                      <span className="text-volt">${(27.95 * packQty - multiTotal).toFixed(2)}</span>
                      {promo?.percent_off ? (
                        <span className="text-volt"> · with code ${money(multiNow)}</span>
                      ) : null}
                    </p>
                  )}
                </div>
              )}
              {plan.emailTestid && (
                <input
                  type="email"
                  value={firstPackEmail}
                  onChange={(e) => setFirstPackEmail(e.target.value)}
                  placeholder="YOUR EMAIL — ONE INTRO PACK PER CUSTOMER"
                  data-testid={plan.emailTestid}
                  className="mt-6 w-full h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-xs uppercase tracking-[0.2em] text-white placeholder:text-white/30 outline-none transition-colors duration-300"
                />
              )}
              <button
                onClick={() => buy(plan.lookup, plan.lookup === "n001_trial" ? packQty : 1)}
                disabled={loading !== null}
                data-testid={plan.button}
                className={`mt-6 h-14 flex items-center justify-center font-anton uppercase tracking-wider text-base transition-[background-color,color,transform] duration-200 active:scale-[0.98] disabled:opacity-60 ${
                  plan.featured
                    ? "bg-volt text-black hover:bg-volt-dark"
                    : "border border-white/25 text-white hover:bg-white hover:text-black"
                }`}
              >
                {loading === plan.lookup
                  ? "Opening checkout…"
                  : plan.lookup === "n001_trial" && packQty > 1
                    ? `Get ${packQty} packs — $${money(pct ? multiNow : multiTotal)}`
                    : plan.lookup === "n001_trial"
                      ? `Get your first pack — $${money(pct ? firstNow : 14.95)}`
                      : `Subscribe — $${money(pct ? subNow : 23.95)}/mo`}
              </button>
              {plan.emailTestid && (
                <p className="mt-3 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
                  {plan.lookup === "n001_trial" && packQty > 1
                    ? "Postage calculated at checkout"
                    : "One per customer · postage calculated at checkout"}
                </p>
              )}
            </div>
          </ScrollReveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export default Pricing;
